import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Calendar, Share2, Loader2, BookOpen, Phone } from 'lucide-react';
import { LanguageDropdown, ALL_LANG_MAP } from '../components/LanguageDropdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://primeswift.in';

const CONTACT_CTA_TEXT = {
  en: 'For more information please contact us',
  hi: 'अधिक जानकारी के लिए कृपया हमसे संपर्क करें',
  mr: 'अधिक माहितीसाठी कृपया आमच्याशी संपर्क साधा',
  bn: 'আরও তথ্যের জন্য অনুগ্রহ করে আমাদের সাথে যোগাযোগ করুন',
  ta: 'மேலும் தகவலுக்கு எங்களை தொடர்பு கொள்ளுங்கள்',
  te: 'మరింత సమాచారం కోసం దయచేసి మమ్మల్ని సంప్రదించండి',
  gu: 'વધુ માહિતી માટે કૃપા કરીને અમારો સંપર્ક કરો',
  kn: 'ಹೆಚ್ಚಿನ ಮಾಹಿತಿಗಾಗಿ ದಯವಿಟ್ಟು ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ',
  ml: 'കൂടുതൽ വിവരങ്ങൾക്ക് ദയവായി ഞങ്ങളെ ബന്ധപ്പെടുക',
  pa: 'ਹੋਰ ਜਾਣਕਾਰੀ ਲਈ ਕਿਰਪਾ ਕਰਕੇ ਸਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰੋ',
  or: 'ଅଧିକ ତଥ୍ୟ ପାଇଁ ଦୟାକରି ଆମ ସହ ଯୋଗାଯୋଗ କରନ୍ତୁ',
  as: 'অধিক তথ্যৰ বাবে অনুগ্ৰহ কৰি আমাৰ সৈতে যোগাযোগ কৰক',
  ur: 'مزید معلومات کے لیے براہ کرم ہم سے رابطہ کریں',
  sa: 'अधिकसूचनार्थं कृपया अस्मान् सम्पर्कयन्तु',
  ne: 'थप जानकारीको लागि कृपया हामीलाई सम्पर्क गर्नुहोस्',
  sd: 'وڌيڪ معلومات لاءِ مهرباني ڪري اسان سان رابطو ڪريو',
  ks: 'مزید معلوماتو خاطرٕ مہربانی کٔرتھ ساننٕ رابطہ کٔرِو',
  doi: 'होर जानकारी लेई कृपा करियै साढ़े कन्नै संपर्क करो',
  gom: 'चड माहितीखातीर उपकार करून आमकां संपर्क करात',
  mai: 'अधिक जानकारी लेल कृपया हमरा सँ संपर्क करू',
  mni: 'অসিগুম্বা ইনফোর্মেসন ননবদা ঐখোয়দা কন্টেক্ট তৌবিয়ু',
  sat: 'ᱡᱟᱹᱥᱛᱤ ᱡᱟᱱᱟᱣ ᱞᱟᱹᱜᱤᱫ ᱫᱟᱭᱟ ᱠᱟᱛᱮ ᱟᱞᱮ ᱥᱟᱶ ᱥᱟᱹᱜᱟᱹᱭ ᱢᱮ',
  bho: 'अउरी जानकारी खातिर कृपया हमसे संपर्क करीं',
  raj: 'अर जाणकारी खातर किरपा करर म्हारै सूं संपर्क करो',
};

const BlogDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug || params.langOrSlug;
  const lang = params.slug ? params.langOrSlug : undefined;
  const activeLang = lang && ALL_LANG_MAP[lang] ? lang : 'en';

  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableLanguages, setAvailableLanguages] = useState(['en']);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', city: '', type: '' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let blogRes;
        if (activeLang === 'en') {
          blogRes = await axios.get(`${API}/blogs/${slug}`);
        } else {
          try {
            blogRes = await axios.get(`${API}/blogs/lang/${activeLang}/${slug}`);
          } catch {
            blogRes = await axios.get(`${API}/blogs/${slug}`);
          }
        }
        setBlog(blogRes.data);

        // Fetch available languages and recent posts in parallel
        const [langsRes, recentRes] = await Promise.all([
          axios.get(`${API}/blogs/${slug}/languages`).catch(() => ({ data: { available: ['en'] } })),
          axios.get(`${API}/blogs/recent`, { params: { limit: 5, lang: activeLang !== 'en' ? activeLang : undefined } }),
        ]);
        setAvailableLanguages(langsRes.data.available);
        setRecent(recentRes.data.filter(b => b.slug !== slug));

        try {
          const relatedRes = await axios.get(`${API}/blogs/${slug}/related`, { params: { limit: 3, lang: activeLang !== 'en' ? activeLang : undefined } });
          setRelated(relatedRes.data);
        } catch { /* no related */ }
      } catch {
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug, activeLang]);

  // SEO: title, meta, hreflang, canonical
  useEffect(() => {
    if (!blog) return;
    document.title = `${blog.meta_title || blog.title} - Primeswift Solutions`;

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content || '');
    };
    setMeta('name', 'description', blog.meta_description || blog.excerpt || '');
    setMeta('property', 'og:title', blog.meta_title || blog.title);
    setMeta('property', 'og:description', blog.meta_description || blog.excerpt || '');
    setMeta('property', 'og:type', 'article');
    if (blog.featured_image) setMeta('property', 'og:image', blog.featured_image);

    const canonicalUrl = activeLang === 'en' ? `${SITE_URL}/blog/${slug}` : `${SITE_URL}/blog/${activeLang}/${slug}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', canonicalUrl);

    const hreflangData = blog.hreflang || [
      { lang: 'en-IN', href: `${SITE_URL}/blog/${slug}` },
      { lang: 'x-default', href: `${SITE_URL}/blog/${slug}` },
      { lang: 'hi-IN', href: `${SITE_URL}/blog/hi/${slug}` },
      { lang: 'mr-IN', href: `${SITE_URL}/blog/mr/${slug}` },
    ];
    const hrefEls = hreflangData.map(h => {
      const el = document.createElement('link');
      el.setAttribute('rel', 'alternate');
      el.setAttribute('hreflang', h.lang);
      el.setAttribute('href', h.href);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      document.title = 'Primeswift Solutions Pvt Ltd';
      hrefEls.forEach(el => el.remove());
      if (canonical) canonical.remove();
    };
  }, [blog, activeLang, slug]);

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  const getBlogUrl = (s) => activeLang === 'en' ? `/blog/${s}` : `/blog/${activeLang}/${s}`;
  const getListingUrl = () => activeLang === 'en' ? '/blog' : `/blog/${activeLang}`;

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, city, type } = contactForm;
    // Save to backend
    try {
      await axios.post(`${API}/blog-contacts`, {
        name, phone, city, type,
        blog_title: blog?.title || '',
        blog_slug: slug || '',
      });
    } catch { /* still open WhatsApp even if save fails */ }
    // Open WhatsApp chat
    const msg = `Hi, I'm interested in Primeswift Solutions.\n\nName: ${name}\nPhone: ${phone}\nCity: ${city}\nType: ${type}\n\nBlog: ${blog?.title || ''}`;
    const waUrl = `https://wa.me/919579027425?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank');
    setShowContactForm(false);
    setContactForm({ name: '', phone: '', city: '', type: '' });
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      <p className="text-slate-500 text-sm">Loading article...</p>
    </div>
  );
  if (!blog) return null;

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareLinks = [
    { name: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(blog.title + ' ' + shareUrl)}`, color: 'bg-green-600 hover:bg-green-700', icon: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z' },
    { name: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, color: 'bg-blue-600 hover:bg-blue-700', icon: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z' },
    { name: 'X', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog.title)}`, color: 'bg-slate-800 hover:bg-slate-900', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
    { name: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, color: 'bg-blue-700 hover:bg-blue-800', icon: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 2a2 2 0 110 4 2 2 0 010-4z' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <div className="flex items-center justify-between mb-4">
            <Button data-testid="back-to-blog" variant="ghost" size="sm" onClick={() => navigate(getListingUrl())} className="text-white hover:bg-white/10 -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" />All Articles
            </Button>
            <LanguageDropdown activeLang={activeLang} slug={slug} basePath="/blog" availableLanguages={availableLanguages} variant="dark" />
          </div>
          <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 mb-3">{blog.category}</Badge>
          <h1 className="text-2xl md:text-4xl font-bold mb-3 leading-tight" data-testid="blog-detail-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {blog.title}
          </h1>
          <div className="flex items-center gap-4 text-slate-300 text-sm">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{formatDate(blog.created_at)}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className={`grid grid-cols-1 ${recent.length > 0 ? 'lg:grid-cols-3' : ''} gap-8`}>
          {/* Main Content */}
          <div className={`${recent.length > 0 ? 'lg:col-span-2' : ''} space-y-6`}>
            {blog.featured_image && (
              <img src={blog.featured_image} alt={blog.title} className="w-full rounded-xl shadow-md aspect-video object-cover" data-testid="blog-featured-image" />
            )}
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="pt-6 pb-8">
                <div
                  data-testid="blog-content"
                  className="prose prose-slate max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-p:leading-relaxed prose-p:text-slate-700 prose-a:text-blue-600 prose-img:rounded-lg"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </CardContent>
            </Card>

            {/* Contact CTA */}
            <button
              onClick={() => setShowContactForm(true)}
              data-testid="blog-contact-cta"
              className="block w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-center py-4 px-6 rounded-xl font-semibold text-base transition-all shadow-md hover:shadow-lg cursor-pointer"
            >
              <Phone className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              {CONTACT_CTA_TEXT[activeLang] || CONTACT_CTA_TEXT.en}
            </button>

            {/* Share Buttons */}
            <div className="flex items-center gap-3 flex-wrap" data-testid="share-buttons">
              <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Share2 className="w-4 h-4" />Share:</span>
              {shareLinks.map(link => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${link.name.toLowerCase()}`}
                  className={`${link.color} text-white px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d={link.icon} /></svg>
                  {link.name}
                </a>
              ))}
            </div>

            {/* Related Posts */}
            {related.length > 0 && (
              <div data-testid="related-posts">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map(r => (
                    <Card
                      key={r.id}
                      data-testid={`related-${r.slug}`}
                      className="border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group overflow-hidden"
                      onClick={() => navigate(getBlogUrl(r.slug))}
                    >
                      {r.featured_image && (
                        <div className="aspect-video overflow-hidden">
                          <img src={r.featured_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <CardContent className="pt-3 pb-4">
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs mb-2">{r.category}</Badge>
                        <h4 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2 text-sm">{r.title}</h4>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          {recent.length > 0 && (
          <div className="space-y-6 lg:sticky lg:top-8 lg:self-start">
            <Card className="border-slate-100 shadow-sm" data-testid="recent-posts">
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />Recent Posts
                </h3>
                <div className="space-y-4">
                  {recent.slice(0, 5).map(r => (
                    <div
                      key={r.id}
                      data-testid={`recent-${r.slug}`}
                      className="flex gap-3 cursor-pointer group"
                      onClick={() => navigate(getBlogUrl(r.slug))}
                    >
                      {r.featured_image && (
                        <img src={r.featured_image} alt={r.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors line-clamp-2">{r.title}</h4>
                        <p className="text-xs text-slate-400 mt-1">{formatDate(r.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          )}
        </div>
      </div>

      {/* Contact Form Modal */}
      <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Phone className="w-5 h-5 text-green-600" />
              Contact Us on WhatsApp
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                data-testid="contact-name"
                placeholder="Your full name"
                className="h-11 bg-slate-50"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                data-testid="contact-phone"
                placeholder="Your phone number"
                className="h-11 bg-slate-50"
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              <Input
                data-testid="contact-city"
                placeholder="Your city"
                className="h-11 bg-slate-50"
                value={contactForm.city}
                onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={contactForm.type} onValueChange={(v) => setContactForm({ ...contactForm, type: v })} required>
                <SelectTrigger data-testid="contact-type" className="h-11 bg-slate-50">
                  <SelectValue placeholder="Select your type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Driver">Driver</SelectItem>
                  <SelectItem value="Vehicle Owner">Vehicle Owner</SelectItem>
                  <SelectItem value="Fleet Owner">Fleet Owner</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              data-testid="contact-submit"
              className="w-full bg-green-600 hover:bg-green-700 rounded-full h-11 text-base font-semibold"
              disabled={!contactForm.name || !contactForm.phone || !contactForm.city || !contactForm.type}
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
              Chat on WhatsApp
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogDetail;
